import { useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Check, AlertTriangle, Info, AlertCircle, X } from 'lucide-react';
import './Toast.css';

const VARIANT_ICONS = {
  error: <AlertCircle size={16} />,
  success: <Check size={16} />,
  info: <Info size={16} />,
  warning: <AlertTriangle size={16} />
};

const VARIANT_DURATIONS = {
  success: 5000,
  info: 5000,
  warning: 5000,
  error: 5000
};

const Toast = ({ message, variant = 'error', onClose, duration }) => {
  const timeoutRef = useRef(null);
  const startedAtRef = useRef(0);
  const remainingMsRef = useRef(0);

  const resolvedDuration = useMemo(() => {
    if (typeof duration === 'number') {
      return duration;
    }
    return VARIANT_DURATIONS[variant] ?? VARIANT_DURATIONS.error;
  }, [duration, variant]);

  const clearAutoCloseTimer = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const scheduleAutoCloseTimer = useCallback(() => {
    if (!onClose || !remainingMsRef.current || remainingMsRef.current <= 0) {
      return;
    }
    startedAtRef.current = Date.now();
    timeoutRef.current = window.setTimeout(() => {
      clearAutoCloseTimer();
      onClose();
    }, remainingMsRef.current);
  }, [clearAutoCloseTimer, onClose]);

  useEffect(() => {
    clearAutoCloseTimer();

    if (!message || !onClose || !resolvedDuration || resolvedDuration <= 0) {
      remainingMsRef.current = 0;
      return undefined;
    }

    remainingMsRef.current = resolvedDuration;
    scheduleAutoCloseTimer();

    return () => clearAutoCloseTimer();
  }, [clearAutoCloseTimer, message, onClose, resolvedDuration, scheduleAutoCloseTimer]);

  const handleMouseEnter = () => {
    if (!resolvedDuration || resolvedDuration <= 0 || !timeoutRef.current) {
      return;
    }
    const elapsedMs = Date.now() - startedAtRef.current;
    remainingMsRef.current = Math.max(0, remainingMsRef.current - elapsedMs);
    clearAutoCloseTimer();
  };

  const handleMouseLeave = () => {
    if (!resolvedDuration || resolvedDuration <= 0 || timeoutRef.current) {
      return;
    }
    if (remainingMsRef.current <= 0) {
      onClose?.();
      return;
    }
    scheduleAutoCloseTimer();
  };

  if (!message) {
    return null;
  }

  const toast = (
    <div
      className={`toast toast-${variant}`}
      role="status"
      aria-live="polite"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className="toast-icon" aria-hidden="true">{VARIANT_ICONS[variant] || VARIANT_ICONS.error}</span>
      <span className="toast-message">{message}</span>
      <button className="toast-close" type="button" aria-label="Dismiss notification" onClick={onClose}>
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  );

  if (typeof document === 'undefined') {
    return toast;
  }

  return createPortal(toast, document.body);
};

export default Toast;
